import Code from '@/components/dashboard/Code';
import ClientCode from '@/components/dashboard/ClientCode';
import SectionActions from '@/docs/SectionActions';
import Links from '@/docs/ExampleLinks';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function FinancialsAPIDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="Financials API" description="The Financials API provides direct access to company financial statements and key performance metrics.
          Retrieve historical income statement, balance sheet, and cash flow data for publicly..."  canonicalUrl="https://axionquant.com/docs/03-fundamentals/financials/00-snapshot" />
      <div className="mb-10">
        <DocHeading className="text-3xl font-bold text-gray-900 mb-4">Financials API</DocHeading>
        <p className="text-lg text-gray-700 max-w-[900px]">
          The Financials API provides direct access to company financial statements and key performance metrics.
          Retrieve historical income statement, balance sheet, and cash flow data for publicly traded companies.
          All financial figures are sourced from SEC filings and are presented in the company's reporting currency.
          The API supports multi-period requests to enable trend analysis.
        </p>
      </div>
      <div className="space-y-12" ref={sectionRef}>
        {/* ========== 14. Financial Snapshot ========== */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /financials/:ticker/snapshot
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Returns a real-time financial snapshot including current stock price, analyst targets,
                key valuation metrics, and profitability ratios. Data is sourced from market data feeds
                and may be delayed by up to 15 minutes.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Path Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm"><span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">Required</span></td>
                      <td className="px-4 py-3 text-sm text-gray-700">Stock ticker symbol</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <Links links={['/financials/AAPL/snapshot', '/financials/MSFT/snapshot']} />

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Key Response Fields</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">currentPrice</p>
                  <p className="text-sm text-gray-600">Latest trading price</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">targetHighPrice, targetLowPrice, targetMeanPrice, targetMedianPrice</p>
                  <p className="text-sm text-gray-600">Analyst price targets</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">recommendationMean, recommendationKey, numberOfAnalystOpinions</p>
                  <p className="text-sm text-gray-600">Analyst consensus (1=Strong Buy, 5=Strong Sell)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">totalCash, totalCashPerShare</p>
                  <p className="text-sm text-gray-600">Cash and cash equivalents</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">ebitda, totalDebt, totalRevenue</p>
                  <p className="text-sm text-gray-600">Key income statement items</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">quickRatio, currentRatio, debtToEquity</p>
                  <p className="text-sm text-gray-600">Liquidity and leverage ratios</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">returnOnAssets, returnOnEquity</p>
                  <p className="text-sm text-gray-600">Profitability metrics</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">freeCashflow, operatingCashflow</p>
                  <p className="text-sm text-gray-600">Cash flow metrics</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">earningsGrowth, revenueGrowth</p>
                  <p className="text-sm text-gray-600">Year-over-year growth rates</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">grossMargins, ebitdaMargins, operatingMargins, profitMargins</p>
                  <p className="text-sm text-gray-600">Profit margin percentages</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">financialCurrency</p>
                  <p className="text-sm text-gray-600">Reporting currency (usually USD)</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Financial Snapshot (Apple)" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">Financial Snapshot (Apple)</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="financials/ticker/snapshot"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="03-fundamentals/financials/snapshot"></Code>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
